import React, { useState } from "react";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import { businessCardService, chatActionService } from "../../services/api";
import toast from "react-hot-toast";
import useChatTimer from "../../hooks/inbox/useChatTimer";
import { DEFAULT_INITIAL_DAYS } from "../../utils/chatTimer";

const CustomMessageInput = () => {
  const { channel } = useChannelStateContext();
  const { client } = useChatContext();

  // 5e canon: the placeholder is personalised — "Message <first name>…". The
  // counterpart is the other 1:1 member; groups/unknown fall back to the
  // generic wording. Marin feedback 2026-07-16.
  const counterpartFirstName = React.useMemo(() => {
    try {
      const members = Object.values(channel?.state?.members || {});
      const others = members.filter(
        (m) => String(m?.user?.id) !== String(client?.userID)
      );
      if (others.length !== 1) return null;
      const name = others[0]?.user?.name || "";
      return name.trim().split(/\s+/)[0] || null;
    } catch (e) {
      return null;
    }
  }, [channel?.state?.members, client?.userID]);

  // The "Share business card" button used to be gated on an OR of five contact
  // codenames. Three of them (contact_scouts_other_clubs, contact_head_scout,
  // internal_contact_everyone) were struck in V3/B9 — internal contact is
  // canonically free, so the platform stopped reporting them. The gate is gone
  // entirely rather than shortened, for two reasons:
  //
  //   1. Marin's ruling is that this gate should not exist at all. The two
  //      survivors, contact_club and contact_agents, gate OPENING a chat with
  //      an outside club or agent (dashboard/views.create_club_chat and the
  //      agent chat doors). This button does something else: it posts the
  //      user's OWN business card into a channel they are already a member of.
  //      Membership is the real gate and it is enforced by the channel, the
  //      frozen flag and the lifecycle timer, all of which still apply below.
  //   2. Shortening the OR would not have kept the button where it was.
  //      internal_contact_everyone was seeded to all nine role families, so the
  //      OR was always true; contact_club/contact_agents are seeded only to
  //      CEO, Sporting Director, Technical Director, Club Admin and Head of
  //      Scouting. Leaving those two would have taken the button away from
  //      Scout Team, Co Trainer / Assistant Coach, Head Coach, Default Staff
  //      Role (whose seeded set is now empty) and club-less staff — including
  //      the very seat the fix was meant to protect.
  //
  // Side effect, deliberate and reported: agents, agency admins, lawyers and
  // players hold no club codenames, so they never saw this button. Now they do.

  const [messageText, setMessageText] = useState("");
  const [isAttachingFile, setIsAttachingFile] = useState(false);
  const [isSendingBusinessCard, setIsSendingBusinessCard] = useState(false);
  const [isReopening, setIsReopening] = useState(false);

  // Check if chat is frozen (closed)
  const isFrozen = channel?.data?.frozen === true;

  // Club<->Agent lifecycle timer. A timer-expired chat reuses the frozen
  // read-only rendering but swaps in timer copy and a club-only revive; a
  // manually-closed (non-timer) chat keeps the existing close/reopen banner.
  const timer = useChatTimer(channel);
  const isTimerExpired = timer.isTimerChat && timer.isExpired;

  // Expiry date for the archived banner (design 1o: "expired on 28 Jun").
  // en-GB gives "28 Jun"; omitted gracefully when the deadline is unknown.
  const expiredDateLabel = timer.expiresAtMs
    ? new Date(timer.expiresAtMs).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      })
    : null;

  const handleReviveConversation = async () => {
    await timer.revive();
  };

  // Handle reopening chat
  const handleReopenChat = async () => {
    setIsReopening(true);
    try {
      if (!channel) {
        throw new Error("No channel selected");
      }

      const result = await chatActionService.performChannelAction(
        channel.id,
        "open"
      );

      if (result.success) {
        toast.success("Chat reopened successfully");
      } else {
        throw new Error(result.error || "Failed to reopen chat");
      }
    } catch (error) {
      console.error("Error reopening chat:", error);
      toast.error(error.message || "Failed to reopen chat");
    } finally {
      setIsReopening(false);
    }
  };

  // Handle sending message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !channel) return;

    try {
      await channel.sendMessage({
        text: messageText.trim(),
      });
      setMessageText("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle file attachment
  const handleFileAttachment = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.pdf,.doc,.docx,.txt,.xlsx,.ppt,.pptx,.zip,.rar";
    input.multiple = true;

    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length || !channel) return;

      setIsAttachingFile(true);

      try {
        for (const file of files) {
          console.log("Processing file:", file.name, file.type, file.size);

          // Try the direct upload approach first
          try {
            if (file.type.startsWith("image/")) {
              // Upload image and get URL
              const response = await channel.sendImage(file);
              console.log("Image upload response:", response);

              // Send message with image attachment
              await channel.sendMessage({
                text: `📷 ${file.name}`,
                attachments: [
                  {
                    type: "image",
                    image_url: response.file,
                    fallback: file.name,
                  },
                ],
              });
            } else {
              // Upload file and get URL
              const response = await channel.sendFile(file);
              console.log("File upload response:", response);

              // Send message with file attachment
              await channel.sendMessage({
                text: `📎 ${file.name}`,
                attachments: [
                  {
                    type: "file",
                    asset_url: response.file,
                    title: file.name,
                    file_size: file.size,
                    mime_type: file.type,
                  },
                ],
              });
            }
          } catch (uploadError) {
            console.error(
              "Upload method failed, trying alternative:",
              uploadError
            );

            // Alternative approach: Create FormData and upload
            const formData = new FormData();
            formData.append("file", file);

            try {
              // Use the client's uploadFile method if available
              if (client.uploadFile) {
                const uploadResponse = await client.uploadFile(
                  formData.get("file"),
                  channel.type,
                  channel.id
                );

                await channel.sendMessage({
                  text: `📎 ${file.name}`,
                  attachments: [
                    {
                      type: file.type.startsWith("image/") ? "image" : "file",
                      ...(file.type.startsWith("image/")
                        ? { image_url: uploadResponse.file }
                        : { asset_url: uploadResponse.file }),
                      title: file.name,
                      fallback: file.name,
                    },
                  ],
                });
              } else {
                throw new Error("No upload method available");
              }
            } catch (altError) {
              console.error("Alternative upload also failed:", altError);
              throw altError;
            }
          }
        }
      } catch (error) {
        console.error("Error sending file:", error);
        alert(
          `Failed to send file(s). Please check the console for details. Error: ${error.message}`
        );
      } finally {
        setIsAttachingFile(false);
      }
    };

    input.click();
  };


  // Handle contact/person functionality
  const handleContactAction = async () => {
    if (!channel) return;

    setIsSendingBusinessCard(true);
    try {
      // Share business card
      const res = await businessCardService.generateShareHtml();
      if (res.success) {
        const { contact_card_html, business_card_id } = res.data;
        await channel.sendMessage({
          text: "💼 My business card",
          attachments: [
            {
              type: "business-card",
              html: contact_card_html,
              business_card_id,
            },
          ],
        });
        toast.success("Business card sent successfully");
      } else {
        toast.error(res.error || "Failed to share business card");
        console.error("Failed to share business card:", res.error);
      }
    } catch (error) {
      console.error("Error sharing business card:", error);
      toast.error("Failed to send business card. Please try again.");
    } finally {
      setIsSendingBusinessCard(false);
    }
  };

  return (
    <div className="border-t border-[#EBEDF0] bg-white shadow-[0px_4px_40px_0px_rgba(0,0,0,0.04)]">
      {/* Timer-expired conversation: read-only archive. History stays readable;
          the write-lock is enforced backend-side. Only the club sees revive. */}
      {isTimerExpired && (
        <div
          className="px-4 py-3 bg-gray-50 border-b border-[#E6E6E6] flex items-center justify-between gap-3"
          data-testid="chat-timer-expired-banner"
        >
          <div className="flex items-center gap-2 text-sm text-[#6C737F]">
            <svg
              className="w-5 h-5 text-[#6C737F] flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <div className="flex flex-col">
              <span className="font-medium">
                {/* Date kept in its own data-no-translate node (chip contract)
                    so the text-matching translator never sees the interpolated
                    date inside the sentence. */}
                {expiredDateLabel ? (
                  <>
                    <span>This conversation expired on</span>{" "}
                    <span data-no-translate="true">{expiredDateLabel}</span>{" "}
                    <span>and is read-only.</span>
                  </>
                ) : (
                  "This conversation expired and is read-only."
                )}
              </span>
              <span className="text-xs text-[#9CA3AF]">
                {/* Controller sub-line branches on the timer CONTEXT (M4-F3):
                    "accepted this request" is Last-Minute agent_agent wording;
                    the club controller gets the revive window from config
                    (number out of the translator's reach, like the chip). */}
                {timer.canRevive ? (
                  timer.isAgentAgent ? (
                    "You accepted this request. Reactivating starts a fresh 14-day timer."
                  ) : (
                    <>
                      <span>Reactivating reopens this conversation with a fresh</span>{" "}
                      <span data-no-translate="true">{DEFAULT_INITIAL_DAYS}</span>
                      <span>-day window.</span>
                    </>
                  )
                ) : (
                  "The rights holder can reactivate this conversation."
                )}
              </span>
            </div>
          </div>

          {/* Reactivate (design 1o) is rights-holder-only — gated on can_revive
              from the backend; the other party sees the banner without it. */}
          {timer.canRevive && (
            <button
              onClick={handleReviveConversation}
              disabled={timer.isMutating}
              className="px-4 py-2 text-sm font-medium text-white bg-[#2283FF] rounded-lg hover:bg-[#1a6acc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
              aria-label="Reactivate chat"
              data-testid="chat-timer-revive-button"
            >
              {timer.isMutating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Reactivating…</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                  <span>Reactivate chat</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Show banner when chat is manually frozen (existing close/reopen). */}
      {isFrozen && !isTimerExpired && (
        <div className="px-4 py-3 bg-gray-50 border-b border-[#E6E6E6] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-[#6C737F]">
            <svg
              className="w-5 h-5 text-[#6C737F] flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span className="font-medium">This chat has been closed.</span>
          </div>

          {/* Reopen button in banner */}
          <button
            onClick={handleReopenChat}
            disabled={isReopening}
            className="px-4 py-2 text-sm font-medium text-white bg-[#2283FF] rounded-lg hover:bg-[#1a6acc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
            aria-label="Reopen chat"
          >
            {isReopening ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Reopening...</span>
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                  />
                </svg>
                <span>Reopen Chat</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Right padding at md+ keeps the Send button clear of the global
          floating support widget (App.js: 64px button, 24px edge inset ≈ 88px),
          which otherwise covers Send at wide, single-pane widths (parity #37).
          The reserved strip is exactly where the widget sits, so it reads as
          intentional rather than empty. */}
      <div className="p-4 md:pr-[92px]">
        <div className="flex items-center gap-[9px] w-full">
          {/* 5e canon (Marin 2026-07-16): attach + camera sit LEFT of the field,
              the solid-blue Send is the only control on the RIGHT. */}
          <div className="flex items-center gap-[9px]">
            {/* Paperclip / attach-document button */}
            <button
              onClick={handleFileAttachment}
              disabled={isFrozen || isAttachingFile}
              className="w-9 h-9 flex items-center justify-center border border-[#E4E9F0] bg-white rounded-[9px] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 group relative"
              aria-label="Attach file"
              title={isFrozen ? "Chat is closed" : "Attach document"}
            >
              {isAttachingFile ? (
                <div className="w-4 h-4 border-2 border-[#2283FF] border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg
                  className="w-[15px] h-[15px] text-[#475467]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
              )}
            </button>

            

            {/* Share your own business card. Ungated on purpose — see the note
                where the old five-codename OR used to live. */}
            <button
                onClick={handleContactAction}
                disabled={
                  isFrozen ||
                  isAttachingFile ||
                  isSendingBusinessCard
                }
                className="w-9 h-9 flex items-center justify-center border border-[#E4E9F0] bg-white rounded-[9px] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 group relative"
                aria-label="Share business card"
                title={isFrozen ? "Chat is closed" : "Share business card"}
              >
                {isSendingBusinessCard ? (
                  <div className="w-[15px] h-[15px] border-2 border-[#2283FF] border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg
                    className="w-[15px] h-[15px] text-[#475467]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                )}
            </button>

            
          </div>

          <div className="flex-1">
            <input
              type="text"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isTimerExpired
                  ? "Messaging is off for archived chats"
                  : isFrozen
                  ? "Chat is closed"
                  : counterpartFirstName
                  ? `Message ${counterpartFirstName}…`
                  : "Leave a message…"
              }
              className="w-full px-[13px] py-[9px] text-[13px] text-[#1E1E1E] bg-[#F5F7FA] border border-[#E9EDF2] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#2283FF] focus:border-transparent placeholder:text-[#98A2B3] placeholder:text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isFrozen || isAttachingFile}
            />
          </div>

          {/* Send — solid #2283FF 36×36 r9, white arrow (design 3d) */}
            <button
              onClick={handleSendMessage}
              disabled={
                isFrozen ||
                !messageText.trim() ||
                isAttachingFile ||
                isAttachingFile
              }
              className="w-9 h-9 flex items-center justify-center rounded-[9px] bg-[#2283FF] hover:bg-[#1a6acc] disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-150"
              aria-label="Send message"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7L12 2L8.5 12L6.5 8L2 7Z"
                  stroke="#fff"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
        
        </div>
      </div>
    </div>
  );
};

export default CustomMessageInput;
